// Parsing helpers for the hand-authored `pageSchemaCode` field that editors
// paste into WordPress (synced onto page/taxonomy Typesense documents).
//
// The field is NOT a single JSON object: it is 1–4 separate top-level JSON-LD
// objects concatenated together (usually separated by blank lines). Each root
// object must be rendered in its OWN <script type="application/ld+json"> tag.

// Yoast already emits Organization / BlogPosting / WebPage / BreadcrumbList
// etc. via `seoFullHead`. Re-rendering those from `pageSchemaCode` produces
// conflicting duplicate schema, so we only render the hand-authored types the
// content team is meant to add. Extend this set as new supported types appear.
export const ALLOWED_SCHEMA_TYPES = new Set<string>([
  'FAQPage',
  'ItemList',
  'HowTo',
  'QAPage',
]);

/**
 * Split concatenated top-level JSON objects using brace-counting that respects
 * braces appearing inside strings. Blank-line splitting is unreliable (values
 * can contain blank lines, and leading newlines vary), so we scan characters.
 */
function splitJsonLdBlocks(raw: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let start = -1;
  let inStr = false;
  let esc = false;

  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];

    if (inStr) {
      if (esc) esc = false;
      else if (ch === '\\') esc = true;
      else if (ch === '"') inStr = false;
      continue;
    }

    if (ch === '"') {
      inStr = true;
    } else if (ch === '{') {
      if (depth === 0) start = i;
      depth++;
    } else if (ch === '}') {
      depth--;
      if (depth === 0 && start !== -1) {
        out.push(raw.slice(start, i + 1));
        start = -1;
      }
    }
  }

  return out;
}

/**
 * Parse `pageSchemaCode` into individual JSON-LD objects. Invalid blocks are
 * silently dropped (never crash the page) and only allow-listed @types are
 * kept so we don't duplicate schema Yoast already emits via `seoFullHead`.
 */
export function parsePageSchema(
  raw?: string | null,
): Record<string, unknown>[] {
  if (!raw || typeof raw !== 'string') return [];

  return splitJsonLdBlocks(raw)
    .map((block) => {
      try {
        return JSON.parse(block) as Record<string, unknown>;
      } catch {
        return null; // drop invalid blocks
      }
    })
    .filter((obj): obj is Record<string, unknown> => Boolean(obj))
    .filter((obj) => {
      const rawType = obj['@type'];
      const types = Array.isArray(rawType) ? rawType : [rawType];
      return types.some(
        (t) => typeof t === 'string' && ALLOWED_SCHEMA_TYPES.has(t),
      );
    })
    // Editors sometimes omit @context; without it validators resolve the
    // @type names against the page URL instead of schema.org.
    .map((obj) =>
      obj['@context'] ? obj : { '@context': 'https://schema.org', ...obj },
    );
}
